/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { InputDialog } from "./InputDialog";
import FailureDialog from "./FailureDialog";
import { SearchDialog } from "./SearchDialog";
import { SaveOrCancelDialog } from "./SaveOrCancelDialog";
import { OpenLinkDialog } from "./OpenLinkDialog";
import { WarningDialog } from "./WarningDialog";
import { BlackListedFileDialog } from "./BlackListedFileDialog";
import { UnavailableQuickToolsFeature } from "./UnavailableQuickToolsFeature";
import { PrintOrCancelDialog } from "./PrintOrCancelDialog";
import { CopyTextDialog } from "./CopyTextDialog";
import { UnsupportedFeaturesDialog } from "./UnsupportedFeaturesDialog";
import { DocumentPropertiesDialog } from "./DocumentPropertiesDialog";
import InvalidValueDialog from "dc-preview-utils/src/components/dialog/InvalidValueDialog";
import * as stores from "stores";
import { isInitialPageLoaded, getPagesViewYPos } from "selectors/pagedViewState";
import { getDocumentVisibleHeight, getViewPortHeight } from "selectors/documentViewSelectors";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { triggerDialogOpenEvent, triggerDialogCloseEvent } from "publicApis/Events/events";
import { isFullScreenMode } from "selectors/documentSelectors";
import { exitFullScreenView } from "utils/PageByPageViewUtils";
import Dialog from "dc-preview-utils/src/components/dialog";
import { fullScreenExitReason } from "constants/enums";
import { DeletePageConfirmationDialog } from "organize/DeletePageConfirmationDialog";
import { OrganizeErrorHandlingDialog } from "organize/OrganizeErrorHandlingDialog";

export const dialogTypes = Object.freeze({
  INPUT_DIALOG: InputDialog,
  FAILURE_DIALOG: FailureDialog,
  SEARCH_DIALOG: SearchDialog,
  SAVE_OR_CANCEL_DIALOG: SaveOrCancelDialog,
  OPEN_LINK_DIALOG: OpenLinkDialog,
  BLACKLISTED_FILE_DIALOG: BlackListedFileDialog,
  COPY_TEXT_DIALOG: CopyTextDialog,
  UNSUPPORTED_FEATURES_DIALOG: UnsupportedFeaturesDialog,
  INVALID_VALUE_DIALOG: InvalidValueDialog,
  UNAVAILABLE_QUICK_TOOLS_FEATURE: UnavailableQuickToolsFeature,
  PRINT_OR_CANCEL_DIALOG: PrintOrCancelDialog,
  WARNING_DIALOG: WarningDialog,
  DOCUMENT_PROPERTIES_DIALOG: DocumentPropertiesDialog,
  DELETE_PAGE_CONFIRMATION_DIALOG: DeletePageConfirmationDialog,
  ORGANIZE_ERROR_HANDLING_DIALOG: OrganizeErrorHandlingDialog
});

class DialogApi extends Dialog {
  constructor() {
    super();
    this.dialogPromiseResolve = () => {};
    this.dialogPromise = Promise.resolve();
  }

  resetDialogPromise = () =>
    new Promise(resolve => {
      this.dialogPromiseResolve = resolve;
    });

  show = (DialogType, props, dialogStyle = {}) => {
    if (isFullScreenMode(stores)) {
      exitFullScreenView(stores, fullScreenExitReason.DIALOG);
    }
    this.dialogPromise = this.dialogPromise.then(() => {
      if (getIsInPlaceMode(stores) && isInitialPageLoaded(stores)) {
        const vh = getViewPortHeight(stores);
        // Bug fix: [ADCS-25449]: vh / 50 is added to compensate 2vh transform in the .spectrum-Dialog.is-open class.
        const dialogTop = getDocumentVisibleHeight(stores) / 2 + getPagesViewYPos(stores) + vh / 50;
        dialogStyle.top = `${dialogTop}px`;
      }
      const isDialogOpened = super.show(DialogType, props, dialogStyle);
      if (isDialogOpened) {
        stores.documentStore.setdialogOpened(true);
        triggerDialogOpenEvent();
      }
      return this.resetDialogPromise();
    });
  };

  hide = () => {
    const isDialogHidden = super.hide();
    if (isDialogHidden) {
      stores.documentStore.setdialogOpened(false);
      triggerDialogCloseEvent();
    }
    this.dialogPromiseResolve();
  };
}

export const dialogApi = new DialogApi();
